import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, AlertTriangle, RefreshCw } from "lucide-react";
import { ClaimButton, Disposition, Explanations, ReleaseButton } from "@/components/LtDisposition";

const fmtPhone = (p) => (p && p.length === 10 ? `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}` : p || "");
const label = (k) => k.replace(/[_.]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/\b\w/g, (c) => c.toUpperCase());

const AllFields = ({ l }) => {
  const [open, setOpen] = useState(true);
  const rows = Object.entries(l.all_fields || {});
  if (!rows.length) return null;
  return (
    <div className="mt-2 rounded-md bg-slate-50 p-2" data-testid={`lt-lead-details-${l.id}`}>
      <button type="button" className="text-xs font-semibold text-slate-700 underline" onClick={() => setOpen(!open)} data-testid={`lt-lead-details-toggle-${l.id}`}>
        {open ? "Hide" : "Show"} all {rows.length} details from Zapier
      </button>
      {open && (
        <dl className="mt-1 grid grid-cols-1 gap-x-6 gap-y-0.5 text-xs sm:grid-cols-2 lg:grid-cols-3">
          {rows.map(([k, v]) => (
            <div key={k} className="flex min-w-0 gap-1"><dt className="shrink-0 text-slate-500">{label(k)}:</dt><dd className="break-words text-slate-900">{v}</dd></div>
          ))}
        </dl>
      )}
    </div>
  );
};

const LeadRow = ({ l, admin, reload }) => {
  const addr = [l.property_address, l.city, [l.state, l.zip_code].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return (
    <div className="border-t border-slate-100 py-3 text-sm" data-testid={`lt-lead-${l.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-slate-900" data-testid={`lt-lead-name-${l.id}`}>{l.name || "Unknown caller"}</span>
        <Badge className="border-0 bg-slate-100 text-slate-700" data-testid={`lt-lead-call-${l.id}`}>{l.lt_call_status || "Call record"}</Badge>
        {l.assigned && <Badge className="border-0 bg-emerald-50 text-emerald-800" data-testid={`lt-lead-stage-${l.id}`}>{l.lt_disposition || "New Lead"}</Badge>}
        {!l.assigned && !admin && !l.lt_returned && <ClaimButton l={l} onDone={reload} />}
      </div>
      <div className="mt-1 grid grid-cols-1 gap-x-6 gap-y-0.5 text-slate-700 sm:grid-cols-2">
        {l.phone && <a href={`tel:${l.phone}`} className="text-slate-900 underline" data-testid={`lt-lead-phone-${l.id}`}>{fmtPhone(l.phone)}</a>}
        {l.email && <span data-testid={`lt-lead-email-${l.id}`}>{l.email}</span>}
        {addr && <span data-testid={`lt-lead-address-${l.id}`}>{addr}</span>}
        {(l.loan_purpose || l.opportunity_type) && <span>{l.loan_purpose || l.opportunity_type}{l.requested_loan_amount ? ` · $${Number(l.requested_loan_amount).toLocaleString()}` : ""}</span>}
      </div>
      <div className="mt-1 text-xs text-slate-500">{new Date(l.created_at).toLocaleString()} · <span className="font-medium text-slate-700" data-testid={`lt-lead-assigned-${l.id}`}>Assigned to: {l.mlo_name || "Not assigned yet"}</span></div>
      {!!l.lt_conflicts?.length && <div className="mt-1 text-xs text-amber-700">Kept original values for: {l.lt_conflicts.map((c) => c.field).join(", ")}</div>}
      {l.mine && !l.lt_returned && <Disposition l={l} onDone={reload} />}
      {l.mine && !l.lt_returned && <ReleaseButton l={l} onDone={reload} />}
      {(l.mine || admin) && <Explanations l={l} />}
      <AllFields l={l} />
    </div>
  );
};

const ReviewQueue = () => {
  const [items, setItems] = useState([]);
  const load = () => { api.get("/admin/live-transfers/review").then((r) => setItems(r.data.items)).catch(() => {}); };
  useEffect(() => { load(); }, []);
  const dismiss = async (id) => { try { await api.post(`/admin/live-transfers/review/${id}/dismiss`); load(); } catch (e) { toast.error(apiError(e)); } };
  if (!items.length) return null;
  return (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="lt-review-queue">
      <div className="mb-1 flex items-center gap-2 font-semibold text-amber-900"><AlertTriangle className="h-4 w-4" />Needs review: {items.length} payload(s) couldn't be read</div>
      {items.map((i) => (
        <div key={i.id} className="border-t border-amber-200 py-2 text-xs" data-testid={`lt-review-${i.id}`}>
          <div className="font-semibold text-amber-900">{i.error}</div>
          <pre className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap break-all text-amber-800">{JSON.stringify(i.payload).slice(0, 600)}</pre>
          <Button size="sm" variant="ghost" className="h-7" onClick={() => dismiss(i.id)} data-testid={`lt-review-dismiss-${i.id}`}>Dismiss</Button>
        </div>
      ))}
    </div>
  );
};

export const LiveTransferLeads = ({ admin = false }) => {
  const [leads, setLeads] = useState(null);
  const load = () => { api.get("/live-transfers/leads").then((r) => setLeads(r.data.leads)).catch((e) => toast.error(apiError(e))); };
  const [busy, setBusy] = useState(false);
  useEffect(() => { load(); }, []);
  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post("/live-transfers/refresh");
      toast.success(r.data.recovered ? `Recovered ${r.data.recovered} live transfer lead(s)` : "Live transfer data is up to date");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Card className="mb-5 p-6" data-testid="lt-leads-card">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Users className="h-5 w-5" />Live transfer leads</h3>
        <Button size="sm" variant="outline" onClick={refresh} disabled={busy} data-testid="lt-leads-refresh-btn">
          <RefreshCw className={`mr-1 h-4 w-4 ${busy ? "animate-spin" : ""}`} />{busy ? "Refreshing..." : "Refresh data"}
        </Button>
      </div>
      <p className="mb-3 text-sm text-slate-600">All live transfer caller details, shown to every loan officer with the loan officer each transfer is assigned to.</p>
      {admin && <ReviewQueue />}
      {leads && !leads.length && <p className="text-sm text-slate-500" data-testid="lt-leads-empty">No live transfer leads yet.</p>}
      {leads?.map((l) => <LeadRow key={l.id} l={l} admin={admin} reload={load} />)}
    </Card>
  );
};
